import React, { useEffect, useRef } from "react";

/**
 * How long the screen is held before it hands over.
 *
 * Long enough for the tick to finish drawing and be read as an answer - the
 * badge and the mark together take 600ms - and short enough that it never
 * becomes a screen somebody is waiting on. A payment app holds its tick for
 * about this long, and the number is here rather than at the call site so the
 * screen owns its own timing.
 */
export const SUCCESS_HOLD_MS = 1500;

export interface OtpSuccessProps {
  /** The address the code was sent to, shown back as what was verified. */
  email?: string;
  /** Called once the hold is up. The caller navigates from here. */
  onDone: () => void;
}

/**
 * What a phone is shown the moment a code is accepted: a tick, and then the
 * next screen.
 *
 * A screen rather than nothing, and that is the whole point of it. Verifying a
 * code is the one step of signing up where the user has done something they
 * cannot check - they typed six digits off a message and have no way of knowing
 * whether they got them right. Going straight from the boxes to the next form
 * answers that only by implication. A phone app answers it outright, and this
 * is that answer.
 *
 * It takes itself away. There is no button, because there is no decision here -
 * pressing "Continue" under a tick is a tap that asks the user to confirm
 * something the app already knows. And it is an overlay rather than a route, so
 * the back gesture cannot land on a confirmation for a step that is behind the
 * user; the history entry the OTP screen occupies is replaced by whatever comes
 * next, exactly as it was before this screen existed.
 *
 * Phone only, from `VerifyOtpPage`. On a desktop the same beat would be a full
 * white browser window interrupting a form flow for a second and a half, which
 * is a stall rather than a confirmation.
 */
const OtpSuccess: React.FC<OtpSuccessProps> = ({ email, onDone }) => {
  // Kept in a ref so a re-render cannot restart the clock, and so the timer is
  // still cleared if the screen goes early - a navigation firing after the
  // component is gone would be a state update on nothing.
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    const timer = setTimeout(() => done.current(), SUCCESS_HOLD_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    // `role="status"` rather than an alert: it is the outcome of something the
    // user just did, not an interruption. A screen reader gets the heading and
    // the line under it announced without the tick, which is decorative - the
    // words say the same thing.
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-white px-8 pb-[calc(env(safe-area-inset-bottom)_+_2rem)] pt-[calc(env(safe-area-inset-top)_+_2rem)] animate-fade-in"
    >
      {/* The screen's own light, centred on the badge. The confirmation is one
          mark on an otherwise empty white screen, and without this the teal
          disc reads as a sticker on a page; with it the colour is coming off
          the mark and the screen belongs to it. Clipped by `overflow-hidden`
          above so it cannot widen the page on a narrow handset. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-[60%] rounded-full bg-[radial-gradient(circle,#CFF4F7_0%,rgba(207,244,247,0)_65%)]"
      />

      <div className="relative flex h-24 w-24 items-center justify-center">
        {/* The ring leaving the disc. Purely decorative and behind it, so it
            cannot catch a stray tap on its way out. */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-brand animate-halo-out motion-reduce:hidden"
        />
        <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-brand animate-badge-pop motion-reduce:animate-none">
          <svg
            viewBox="0 0 32 32"
            fill="none"
            aria-hidden
            className="h-11 w-11"
          >
            {/* Dashed to the path's own length (19.8 units, rounded up), so
                the animation draws the mark rather than fading it in. The
                attributes are the finished state: with the animation off, the
                tick is simply there. */}
            <path
              d="M9 16.8 13.6 21.4 23 12"
              stroke="#FFFFFF"
              strokeWidth={2.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="20"
              strokeDashoffset="0"
              className="animate-tick-draw motion-reduce:animate-none"
            />
          </svg>
        </span>
      </div>

      {/* The delay is an inline style rather than an arbitrary utility: the
          `animate-*` class is the `animation` shorthand, which resets the delay
          to zero, and whether a utility lands after it in the sheet is down to
          generation order. The attribute always wins. */}
      <h1
        style={{ animationDelay: "320ms" }}
        className="mt-7 text-center text-[26px] font-semibold leading-tight tracking-tight text-ink animate-rise-in motion-reduce:animate-none"
      >
        Email verified
      </h1>
      <p
        style={{ animationDelay: "400ms" }}
        className="mt-2 max-w-[19rem] text-center text-[15px] leading-6 text-ink-muted animate-rise-in motion-reduce:animate-none"
      >
        {email ? (
          <>
            <span className="font-medium text-ink">{email}</span> is confirmed.
            Setting up your workspace&hellip;
          </>
        ) : (
          <>Your email is confirmed. Setting up your workspace&hellip;</>
        )}
      </p>

      {/* How much of the hold is left, running for exactly as long as the
          timer above. The duration comes from the same constant rather than
          being written again in CSS, so the bar cannot drift out of step with
          the navigation it is describing.

          Decorative: `role="status"` on the screen has already announced the
          outcome in words, and a reader has nothing to do with a bar. */}
      <span
        aria-hidden
        className="relative mt-9 h-1 w-40 overflow-hidden rounded-full bg-[#E4F1F2]"
      >
        <span
          style={{ animationDuration: `${SUCCESS_HOLD_MS}ms` }}
          className="absolute inset-0 origin-left rounded-full bg-brand animate-progress-fill motion-reduce:animate-none"
        />
      </span>
    </div>
  );
};

export default OtpSuccess;
